import React, { useState, forwardRef, useRef, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './styles.css';
import fetchAPI from '../../utils/fetchAPI';
import { getUser } from '../../utils/sessionInfo';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export const RecuperarPasswdRecord = ({ visible, onClose = () => {} }) => {
  const [USER] = useState(getUser());
  const [open, setOpen] = useState(visible);
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const handleTogglePassword1 = () => setShowPassword1(!showPassword1);
  const handleTogglePassword2 = () => setShowPassword2(!showPassword2);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const captchaRef = useRef(null);

  const resetCaptcha = () => {
    setCaptchaToken(null);
    if (window.turnstile && captchaRef.current) {
      window.turnstile.reset(captchaRef.current);
    }
  };

  useEffect(() => {
    setOpen(visible);
  }, [visible]);

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      if (window.turnstile && captchaRef.current) {
        captchaRef.current.innerHTML = '';
        window.turnstile.render(captchaRef.current, {
          sitekey: window.REACT_APP_TURNSTILE_SITE_KEY,
          callback: (token) => setCaptchaToken(token),
          'expired-callback': () => {
            setCaptchaToken(null);
            showSnackbar('El captcha expiró, por favor verifica nuevamente', 'warning');
          },
          'error-callback': () => {
            setCaptchaToken(null);
            showSnackbar('Error en el captcha, intenta de nuevo', 'error');
          },
        });
        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [open]);

  const handleClose = (success = false) => {
    setOpen(false);
    onClose(success);
    setCaptchaAnswer('');
    setCaptchaQuestion('');
    setBirthDate(null);
    setError('');
    setCaptchaToken(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!captchaAnswer || !birthDate || !captchaQuestion) {
      setError('Todos los campos son requeridos');
      resetCaptcha();
      setLoading(false);
      return;
    }
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12}$/;
    if (!passwordRegex.test(newPassword)) {
      setError(
        'La contraseña debe tener exactamente 12 caracteres e incluir al menos una letra mayúscula, una letra minúscula, un número y un carácter especial (@$!%*?&).'
      );
      resetCaptcha();
      setLoading(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      resetCaptcha();
      setLoading(false);
      return;
    }
    if (!newPassword) {
      setError('el campo requerido');
      resetCaptcha();
      setLoading(false);
      return;
    }

    if (!captchaToken) {
      setError('Por favor completa el captcha');
      setLoading(false);
      return;
    }

    setError('');
    setLoading(true);
    const service1 = 'v1/MX/passwd/save-question';
    fetchAPI({
      service: service1,
      protocol: 'POST',
      params: {
        customer_no: USER?.ID,
        question: captchaQuestion,
        response: captchaAnswer,
        birthday_at: birthDate,
        captchaToken: captchaToken,
      },
      callbackThen: () => {
        const service = 'v1/MX/passwd/update-password';
        fetchAPI({
          service: service,
          protocol: 'PUT',
          params: {
            customer_no: USER?.ID,
            newPassword: newPassword,
          },
          callbackThen: (Respond) => {
            setError('');
            setLoading(false);
            resetCaptcha();
            if (Respond?.validate) {
              handleClose(true);
            }
          },
          callbackCatch: () => {
            setError('');
            setLoading(false);
            showSnackbar('Error al actualizar la contraseña', 'error');
            resetCaptcha();
          },
        });
      },
      callbackCatch: () => {
        setLoading(false);
        resetCaptcha();
        handleClose();
      },
    });
  };

  return (
    <div>
      <Modal open={open} onClose={handleClose} aria-labelledby="modal-title" aria-describedby="modal-description">
        <Box sx={style}>
          <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
            En caso de olvidar tu contraseña
          </Typography>

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="captcha-question-label">Pregunta</InputLabel>
                <Select
                  labelId="captcha-question-label"
                  fullWidth
                  value={captchaQuestion}
                  onChange={(e) => setCaptchaQuestion(e.target.value)}
                  label="Pregunta"
                  variant="outlined"
                >
                  <MenuItem value="¿Cuál es el nombre de tu mascota?">¿Cuál es el nombre de tu mascota?</MenuItem>
                  <MenuItem value="¿Cómo se llama tu primera escuela?">¿Cómo se llama tu primera escuela?</MenuItem>
                  <MenuItem value="¿Cuál era la marca de tu primer coche?">¿Cuál era la marca de tu primer coche?</MenuItem>
                  <MenuItem value="¿Cuál es tu comida favorita?">¿Cuál es tu comida favorita?</MenuItem>
                  <MenuItem value="¿Cuál es el nombre de tu mejor amigo/a?">
                    ¿Cuál es el nombre de tu mejor amigo/a?
                  </MenuItem>
                  <MenuItem value="¿Cuál es tu ciudad favorita?">¿Cuál es tu ciudad favorita?</MenuItem>
                  <MenuItem value="¿Cu��l es el nombre de tu madre/padre?">¿Cuál es el nombre de tu madre/padre?</MenuItem>
                  <MenuItem value="¿Cuál es tu deporte favorito?">¿Cuál es tu deporte favorito?</MenuItem>
                  <MenuItem value="¿Cómo se llamaba tu primer profesor de la primaria?">
                    ¿Cómo se llamaba tu primer profesor de la primaria?
                  </MenuItem>
                  <MenuItem value="¿Cuál es el nombre de tu abuelo favorito?">
                    ¿Cuál es el nombre de tu abuelo favorito?
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                type="text"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                label="Respuesta"
                variant="outlined"
              />

              <TextField
                fullWidth
                type={showPassword1 ? 'text' : 'password'}
                security={!showPassword1}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                label={'Nueva contraseña'}
                variant="outlined"
                inputProps={{
                  maxLength: 12,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword1} edge="end">
                        {showPassword1 ? <VisibilityOff fontSize="large" /> : <Visibility fontSize="large" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                type={showPassword2 ? 'text' : 'password'}
                security={!showPassword2}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                label={'Confirmar contraseña'}
                variant="outlined"
                inputProps={{
                  maxLength: 12,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword2} edge="end">
                        {showPassword2 ? <VisibilityOff fontSize="large" /> : <Visibility fontSize="large" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ width: '100%' }}>
                <DatePicker
                  selected={birthDate}
                  onChange={(date) => setBirthDate(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Fecha de nacimiento"
                  className="custom-datepicker full-width-datepicker"
                  maxDate={new Date()}
                  customInput={
                    <TextField
                      fullWidth
                      label="Fecha de nacimiento"
                      variant="outlined"
                      value={birthDate ? birthDate.toLocaleDateString('es-MX') : ''}
                      InputLabelProps={{ shrink: true }}
                    />
                  }
                />
              </Box>

              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
              <div ref={captchaRef} style={{ marginTop: '10px' }}></div>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                sx={{
                  position: 'relative',
                  width: 150,
                  height: 40,
                }}
              >
                {loading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: 'white',
                      position: 'absolute',
                    }}
                  />
                ) : (
                  'Guardar'
                )}
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
